import {
  Box,
  Card,
  debounce,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Popover,
  TextField,
  Theme,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Children, useEffect, useMemo, useState } from "react";
import { Link, useQueryWithStore } from "react-admin";
import { useHistory } from "react-router-dom";
import { from } from "manipula";
import classnames from "classnames";
import { DriverBreadcrumbSearchField } from "./DriverBreadcrumbSearchField";
import { KeyboardBackspace, Search } from "@material-ui/icons";
import styles from "./DriverBreadcrumb.module.css";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import resourceNames from "../../../common/data/resourceNames";

const useStyles = makeStyles((theme: Theme) => ({
  breadcrumbViewContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "5px",
    alignItems: "center",
    justifyContent: "space-between",
  },
  dropdownTypography: {
    padding: theme.spacing(2),
  },
  searchField: {
    width: `calc(100% - ${theme.spacing(2)}px)`,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginBottom: "8px",
  },
  anchorDiv: {
    minHeight: "48px",
    minWidth: "48px",
  },
  popupContainer: {
    overflow: "hidden",
  },
  dropdownResults: {
    overflowY: "auto",
    maxHeight: "75vh",
  },
  marginBottom4: {
    marginBottom: "4px",
  },
  marginDense: {
    marginTop: 0,
    marginBottom: 0,
  },
}));

export interface SelectedDriver {
  id?: number | null;
  driverId?: string | null;
  driverName?: string | null;
}

interface Props {
  driver?: SelectedDriver;
  getBasePath: (driver?: SelectedDriver) => string;
  getLink?: (driver?: SelectedDriver) => string;
  getBackLink?: (driver?: SelectedDriver) => string;
}

interface DriverNameDisplayProps {
  driverId?: string | null;
  driverName?: string | null;
}

interface DriverNameBreadcrumbViewProps {
  children: React.ReactNode;
  getBasePath: (driver?: SelectedDriver) => string;
  selectedDriver?: SelectedDriver;
  getBackLink?: (driver?: SelectedDriver) => string;
}

const DriverNameDisplay = ({
  driverName,
  driverId,
}: DriverNameDisplayProps) => (
  <Box display="flex" flexDirection="row" gridGap={5}>
    <sup>
      <Typography>{driverId}</Typography>
    </sup>
    <Typography variant="h6">{driverName}</Typography>
  </Box>
);

export const DriverNameBreadcrumbView = ({
  children,
  getBasePath,
  selectedDriver,
  getBackLink,
}: DriverNameBreadcrumbViewProps) => {
  const [driversList, setDriversList] = useState<SelectedDriver[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>("");
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const debounceSetSearchText = useMemo(
    () =>
      debounce((nextValue: string) => {
        setDebouncedSearchText(nextValue);
      }, 500),
    []
  );

  useSearchFields(
    {},
    ["driverName", "driverId"],
    resourceNames.ADMIN_DRIVERS_VIEW
  );
  const { data, loading, total } = useQueryWithStore({
    type: "getList",
    resource: resourceNames.ADMIN_DRIVERS_VIEW,
    payload: {
      sort: {
        field: "driverName",
        order: "ASC",
      },
      filter: {
        q: debouncedSearchText,
        status: "Active",
      },
      pagination: {
        page: 1,
        perPage: 1000,
      },
    },
  });

  useEffect(() => {
    if (!data || !total) {
      return;
    }

    const list: SelectedDriver[] = from(
      Object.getOwnPropertyNames(data).map((x) => ({
        id: data[x].id,
        driverId: data[x].driverId,
        driverName: data[x].driverName,
      }))
    )
      .where((x) => x.id)
      .orderBy((x) => x.driverName)
      .toArray();
    setDriversList(list);
  }, [data, total]);

  const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDriverSelected = (driver?: SelectedDriver) => {
    handleClose();
    if (getBasePath && selectedDriver?.id !== driver?.id) {
      history.push(getBasePath(driver));
    }
  };

  const classes = useStyles();
  const dropdownOpen = Boolean(anchorEl);
  return (
    <div className={classes.breadcrumbViewContainer}>
      <>
        {Children.only(children)}
        <div className={styles.wrapper}>
          {getBackLink ? (
            <IconButton
              onClick={() => history.push(getBackLink(selectedDriver))}
            >
              <KeyboardBackspace />
            </IconButton>
          ) : null}
          <TextField
            disabled={!!anchorEl}
            variant="outlined"
            margin="dense"
            className={`appbar-filter appbar-simple-search-field ${
              anchorEl ? "hidden" : ""
            } ${classes.marginDense}`}
            label=""
            value=""
            onClick={handleClick}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          {/* <IconButton onClick={handleClick}>
            <Search />
          </IconButton> */}
          <Popover
            className={classes.popupContainer}
            open={dropdownOpen}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
          >
            <Box>
              <Card>
                <DriverBreadcrumbSearchField
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    debounceSetSearchText(e.target.value);
                  }}
                  loading={loading}
                  className={classes.searchField}
                />
                <Divider className={classes.marginBottom4} />
              </Card>
              <Card
                className={classnames(
                  classes.dropdownResults,
                  "custom-scrollbar"
                )}
              >
                {driversList.length ? (
                  <List dense>
                    {driversList.map((x) => (
                      <ListItem
                        key={x.id}
                        button
                        onClick={() => handleDriverSelected(x)}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" flexDirection="row" gridGap={5}>
                              <sup>{x.driverId}</sup>
                              <Typography variant="body1">
                                {x.driverName}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography className={classes.dropdownTypography}>
                    No drivers found.
                  </Typography>
                )}
              </Card>
            </Box>
          </Popover>
        </div>
      </>
    </div>
  );
};

export const DriverBreadcrumb = ({
  driver,
  getLink,
  getBasePath,
  getBackLink,
}: Props) => {
  const [selectedDriver, setSelectedDriver] = useState<SelectedDriver>();

  useEffect(() => {
    if (driver) {
      setSelectedDriver(driver);
    }
  }, [driver]);

  const link = getLink ? getLink(selectedDriver) : undefined;
  const driverId = selectedDriver?.driverId ?? "";
  const driverName = selectedDriver?.driverName ?? "";
  if (link) {
    return (
      <>
        <DriverNameBreadcrumbView
          getBasePath={getBasePath}
          selectedDriver={selectedDriver}
          getBackLink={getBackLink}
        >
          <Link to={link}>
            <DriverNameDisplay driverId={driverId} driverName={driverName} />
          </Link>
        </DriverNameBreadcrumbView>
      </>
    );
  }

  return (
    <DriverNameBreadcrumbView
      getBasePath={getBasePath}
      selectedDriver={selectedDriver}
    >
      <DriverNameDisplay driverId={driverId} driverName={driverName} />
    </DriverNameBreadcrumbView>
  );
};
